import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsInstance, SeriesPoint } from '../../models/graphs.types';

import { graphic } from 'echarts/core';

type AreaChartTheme = 'blue' | 'orange' | 'green' | 'default';

@Component({
  selector: 'app-area-chart',
  imports: [NgxEchartsModule],
  templateUrl: './area-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaChartComponent {
  readonly data = input.required<SeriesPoint[]>();
  readonly title = input.required<string>();
  readonly yUnit = input<string>('');
  readonly stepped = input<boolean>(false);
  readonly groupId = input<string | undefined>(undefined);
  readonly tooltipLabel = input<string>('');
  readonly theme = input<AreaChartTheme>('default');

  readonly chartInit = output<EChartsInstance>();

  private readonly themes: Record<AreaChartTheme, { line: string; gradient: { offset: number; color: string }[] }> = {
    default: {
      line: '#7AC6DC',
      gradient: [
        { offset: 0, color: 'rgba(122, 198, 220, 0.8)' },
        { offset: 0.5, color: 'rgba(122, 198, 220, 0.6)' },
        { offset: 1, color: 'rgba(122, 198, 220, 0.1)' },
      ],
    },
    blue: {
      line: '#0092BC',
      gradient: [
        { offset: 0, color: 'rgba(0, 146, 188, 0.6)' },
        { offset: 0.5, color: 'rgba(0, 146, 188, 0.4)' },
        { offset: 0.9, color: 'rgba(0, 146, 188, 0.1)' },
      ],
    },
    orange: {
      line: '#FF9F1C',
      gradient: [
        { offset: 0.0, color: 'rgba(255, 159, 28, 0.98)' },
        { offset: 0.3, color: 'rgba(255, 159, 28, 0.88)' },
        { offset: 1, color: 'rgba(255, 159, 28, 0.3)' },
      ],
    },
    green: {
      line: '#9BC53D',
      gradient: [
        { offset: 0.0, color: 'rgba(155, 197, 61, 0.98)' },
        { offset: 0.3, color: 'rgba(155, 197, 61, 0.88)' },
        { offset: 1, color: 'rgba(155, 197, 61, 0.3)' },
      ],
    },
  };

  private makeAreaGradient(stops: { offset: number; color: string }[]): graphic.LinearGradient {
    return new graphic.LinearGradient(0, 0, 0, 1, stops);
  }

  onChartInit(ec: EChartsInstance): void {
    const groupId = this.groupId();
    if (groupId) {
      ec.group = groupId;
    }
    this.chartInit.emit(ec);
  }

  readonly options = computed(() => {
    const x = this.data().map(p => p.x);
    const y = this.data().map(p => p.y);
    const yUnit = this.yUnit();
    const tooltipLabel = this.tooltipLabel();
    const stepped = this.stepped();

    const { line, gradient } = this.themes[this.theme()];

    return {
      animation: true,
      animationDuration: 750,
      animationDurationUpdate: 750,
      animationEasing: 'cubicInOut' as const,
      animationEasingUpdate: 'cubicInOut' as const,
      grid: { left: 40, right: 18, top: 20, bottom: 30 },
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        padding: [12, 16],
        extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);',
        formatter: (params: { axisValue?: string; value?: number; color?: string }[]): string => {
          const data = params[0];
          const date = data.axisValue;
          const value = data.value;
          const color = data.color;

          const label = tooltipLabel || 'Value';

          return `
            <div style="font-size: 13px; color: #374151;">${date}</div>
            <div style="margin-top: 8px;">
              <span style="color: #6b7280;">${label}:</span>
              <span style="color: ${color}; font-weight: 600; margin-left: 4px;">
                ${value}${yUnit}
              </span>
            </div>
          `;
        },
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: x,
        axisLabel: { color: '#9ca3af' },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#9ca3af' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#e5e7eb', type: 'dashed' } },
      },
      series: [
        {
          type: 'line',
          data: y,
          showSymbol: false,
          smooth: true,
          step: stepped ? 'middle' : false,
          color: line,
          lineStyle: { width: 1 },
          areaStyle: {
            // color: gradient[0].color,
            // opacity: 0.5,
            color: this.makeAreaGradient(gradient),
          },
        },
      ],
    };
  });
}
